import { createContext, useContext, lazy, useState, useEffect } from "react";
import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { Box, ContentBlock, Inline, Text, IconCaution, Strong, ButtonIcon, IconRefresh, Loader } from "braid-design-system";
import * as Apollo from "@apollo/client";
import { gql } from "@apollo/client";
const FeatureContext = createContext({
  features: [],
  overrides: [],
  setOverrides: () => {
  },
  loading: false
});
const useFeatureContext = () => useContext(FeatureContext);
const getFeatureOverrideByKey = (features, key) => features == null ? void 0 : features.find((f) => f.key === key);
const getFeatureByKey = (features, key) => features == null ? void 0 : features.find((f) => f.key === key);
const getFeatureOverrideByKeyOrFeatureByKey = (features, overrides, key) => {
  var _a, _b;
  return ((_a = getFeatureOverrideByKey(overrides, key)) == null ? void 0 : _a.value) ?? ((_b = getFeatureByKey(features, key)) == null ? void 0 : _b.value);
};
const useGetFeatureToggleVariant = (key) => {
  const { features, overrides } = useFeatureContext();
  return getFeatureOverrideByKeyOrFeatureByKey(features, overrides, key);
};
const FeatureToggle = ({
  flagKey,
  whenVariant,
  whenOn,
  whenOff
}) => {
  const enabledVariant = useGetFeatureToggleVariant(flagKey);
  return whenVariant === enabledVariant ? whenOn({}) : (whenOff == null ? void 0 : whenOff({})) ?? /* @__PURE__ */ jsx(Fragment, {});
};
const OverridesIndicatorBanner = ({
  appName
}) => {
  const { overrides, setOverrides } = useFeatureContext();
  if (overrides.length === 0) return null;
  const overrideKeys = overrides.map((override) => override.key);
  return /* @__PURE__ */ jsx(
    Box,
    {
      id: "overrides-indicator-banner",
      data: { testid: "overrides-indicator-banner" },
      background: "brandAccentSoftActive",
      padding: "gutter",
      zIndex: "notification",
      children: /* @__PURE__ */ jsx(ContentBlock, { children: /* @__PURE__ */ jsxs(Inline, { space: "small", alignY: "center", align: "center", children: [
        /* @__PURE__ */ jsxs(Text, { icon: /* @__PURE__ */ jsx(IconCaution, {}), children: [
          "There are feature toggle overrides set in ",
          /* @__PURE__ */ jsx(Strong, { children: appName }),
          " ",
          "which are: ",
          /* @__PURE__ */ jsx(Strong, { children: overrideKeys.join(", ") })
        ] }),
        /* @__PURE__ */ jsx(
          ButtonIcon,
          {
            id: "reset-all-overrides",
            data: { testid: "reset-all-overrides" },
            label: "Reset",
            icon: /* @__PURE__ */ jsx(IconRefresh, {}),
            tone: "neutral",
            variant: "transparent",
            onClick: () => {
              setOverrides([]);
            }
          }
        )
      ] }) })
    }
  );
};
const defaultOptions = {};
const FeatureFlagsDocument = gql`
    query FeatureFlags($keys: [String!]!) {
  featureFlags(keys: $keys) {
    key
    reason
    variant
  }
}
    `;
function useFeatureFlagsQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery(FeatureFlagsDocument, options);
}
const getLocalStorageFeatureState = (storageKey) => {
  var _a;
  try {
    return (_a = JSON.parse(localStorage.getItem(storageKey) || "")) == null ? void 0 : _a.features;
  } catch (err) {
    return [];
  }
};
const setLocalStorageFeatureState = (storageKey, features) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ features }));
  } catch (err) {
  }
};
const FeatureToggleController = lazy(
  () => import("./index.chunk2.mjs").then((module) => ({
    default: module.FeatureToggleController
  }))
);
const isServerRendering = () => typeof window === "undefined";
const FeatureToggleProvider = ({
  children,
  appName,
  featureConfigs,
  isOverridesAllowed = false,
  isOverridesIndicatorBannerEnabled = false,
  delayRendering = false
}) => {
  const featureKeys = featureConfigs.map((config) => config.key);
  const { data, loading } = useFeatureFlagsQuery({
    variables: {
      keys: featureKeys
    },
    skip: featureKeys.length === 0
  });
  const overridesStorageKey = `${appName}|features`;
  const [overrides, setOverrides] = useState([]);
  useEffect(() => {
    setOverrides(getLocalStorageFeatureState(overridesStorageKey));
  }, []);
  useEffect(() => {
    setLocalStorageFeatureState(overridesStorageKey, overrides);
  }, [overrides, overridesStorageKey]);
  const remoteFlags = loading ? [] : (data == null ? void 0 : data.featureFlags) || [];
  const features = featureConfigs.map((config) => {
    var _a;
    const { key, variants, name, description, fallback } = config;
    const featureRemoteValue = (_a = remoteFlags.find(
      (remote) => (remote == null ? void 0 : remote.key) === key && (remote == null ? void 0 : remote.reason) !== "ERROR"
    )) == null ? void 0 : _a.variant;
    return {
      key,
      name,
      description,
      variants,
      value: featureRemoteValue && variants.findIndex((value) => value === featureRemoteValue) >= 0 ? featureRemoteValue : fallback
    };
  });
  return /* @__PURE__ */ jsxs(
    FeatureContext.Provider,
    {
      value: {
        features,
        overrides: isOverridesAllowed ? overrides : [],
        setOverrides,
        loading
      },
      children: [
        !isServerRendering() && isOverridesAllowed && isOverridesIndicatorBannerEnabled && /* @__PURE__ */ jsx(OverridesIndicatorBanner, { appName }),
        delayRendering && loading ? /* @__PURE__ */ jsx(Loader, { delayVisibility: true }) : children,
        !isServerRendering() && isOverridesAllowed && /* @__PURE__ */ jsx(FeatureToggleController, {})
      ]
    }
  );
};
const useGetFeatureToggleVariantsForKeys = (keys) => {
  const { features, overrides } = useFeatureContext();
  return keys.reduce((acc, key) => {
    const value = getFeatureOverrideByKeyOrFeatureByKey(
      features,
      overrides,
      key
    );
    return {
      ...acc,
      [key]: value
    };
  }, {});
};
const useFeatureToggleHasAnyOverrides = () => {
  const { overrides } = useFeatureContext();
  return Boolean(overrides.length);
};
export {
  FeatureFlagsDocument,
  FeatureToggle,
  FeatureToggleProvider,
  getFeatureByKey,
  getFeatureOverrideByKey,
  useFeatureContext,
  useFeatureToggleHasAnyOverrides,
  useGetFeatureToggleVariant,
  useGetFeatureToggleVariantsForKeys
};
